import React from 'react';
import { formatDate } from '../ProductHtmlBanner/utils';
import { useNavigate } from '../PlatformFunctionality';
import { BannerBadge } from '../BannerBadge';
import { ProgressiveImage } from '../hooks/useProgressiveImage/useProgressiveImage';
import { HitPriceTag } from '../ProductCompare/HitPriceTag/HitPriceTag';
import { AlgoliaHitTransformer } from '../Algolia/transformers/AlgoliaHitTransformer';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { styles } from './OverlayBox.css';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { SupplierProductBannerProps } from './types';
import BannerTag from '@jbhi-fi/lanyard-ui/components/BannerTag';
import { getPriceText } from '../BannerBadge/utils';

type OverlayBoxProps = {
  product: SupplierProductBannerProps['contentProducts'][0];
  color: 'Black' | 'White';
  tagType?: 'Banner tag' | 'Badge';
  badgeStyle?: 'Red flag' | 'Star burst';
  badge?: '$ off' | '% off' | '1/2 price' | 'While stocks last!' | 'Hot price!' | 'Hot deal!';
  cardVariant: 'mobile' | 'medium' | 'large';
  subtitle?: string | Array<number> | Array<string>;
};

export const OverlayBox = ({ product, color, tagType, badgeStyle, badge, cardVariant, subtitle }: OverlayBoxProps) => {
  const endDate = formatDate(product?.endDate);
  const { product_image, title, handle, pricing } = product?.hit ?? {};
  const navigateTo = useNavigate();
  const isDesktop = useBreakpoint('lg', 'up');
  const isTablet = useBreakpoint('lg', 'down');
  const isPreOrder = product?.hit?.availability?.canPreOrder;
  const isLongText = title ? title.length > 45 : false;
  const wasPrice = pricing?.wasPrice ?? 0;
  const displayPrice = pricing?.displayPriceInc ?? 0;
  const saveAmount = pricing?.saveAmount ?? 0;

  const discountPercent = (wasPrice - displayPrice) / wasPrice;
  const hasPriceOrPercent = ['$ off', '% off'].includes(badge ?? '');
  const pricePercentNumber = getPriceText(badge, saveAmount, wasPrice);
  const isHalfPrice = badge === '1/2 price';
  const isMoreThanHalfPrice = discountPercent >= 0.5;
  const isInvalidBadge = (hasPriceOrPercent && pricePercentNumber === undefined) || (isHalfPrice && !isMoreThanHalfPrice);
  const hasBadge = tagType === 'Badge' && !!badge && !isInvalidBadge;
  const hasDeliveryTag = !!product?.hit?.deliveryTag;

  const navigateToProduct = () => {
    navigateTo(`/products/${handle}`);
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.badgeImageContainer}>
          {tagType === 'Badge' && !isInvalidBadge ? (
            <div className={styles.badgeContainer}>
              <BannerBadge
                badgeStyle={badgeStyle}
                badge={badge}
                displayPrice={product.hit?.pricing?.displayPriceInc ?? 0}
                saveAmount={product.hit?.pricing?.saveAmount ?? 0}
                wasPrice={product.hit?.pricing?.wasPrice ?? 0}
                showWasPrice={product.hit?.pricing?.displayWasPrice}
                variant={isDesktop ? 'small' : 'mobile'}
              />
            </div>
          ) : tagType === 'Banner tag' ? (
            <div>
              <BannerTag label={product?.hit?.banner_tags?.['label']} />
            </div>
          ) : null}
          <ProgressiveImage
            className={styles.image({ hasBadge })}
            {...AlgoliaHitTransformer.toProgressiveImageProps(
              { product_image: product_image ?? '', title: title ?? '' },
              { sizePx: 160 }
            )}
          />
        </div>
        <div className={styles.rightSide}>
          <div
            className={styles.title({ isLongText })}
            onClick={navigateToProduct}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigateToProduct();
              }
            }}
          >
            {title}
          </div>
          {subtitle ? <div className={styles.subtitle({ isWhiteTheme: color === 'White' })}>{subtitle}</div> : null}
          {product.hit ? (
            <HitPriceTag
              hit={product.hit}
              showDelivery
              className={styles.priceTag({ hasDeliveryTag })}
              showEA={product.enableEAPriceBox}
            />
          ) : null}
          <div className={styles.endDateButtonContainer({ isTablet })}>
            <div className={styles.endDateText({ cardVariant })}>
              {endDate ? (
                <>
                  <b>Ends: </b>
                  <p className={styles.endDateTextWrap({ cardVariant })}>{endDate}</p>
                </>
              ) : null}
            </div>
            <div className={styles.buttonContainer}>
              <Button
                onClick={navigateToProduct}
                type="primary"
                label={isPreOrder ? 'Pre-order' : 'Shop now'}
                fullWidth
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
